import React from 'react';
import { Box, TableCell, TableRow, Typography } from '@mui/material';
import type { UsageActionRow } from '../types';
import { formatCount, elementKindLabel } from '../services/usageFormat';
import SchutzZeile from './SchutzZeile';
import DatenTabelle, { zahlenZelle } from './DatenTabelle';
import { nutzerText } from './SeitenTabelle';

/**
 * RITA-1520 (Nutzungserfassung Phase 3b, Epic RITA-1496) — Wutklicks und Totklicks.
 *
 * Zwei Tabellen statt einer: es sind zwei verschiedene Aussagen („hier klickt jemand wütend
 * mehrfach“ gegen „hier klickt jemand auf etwas, das gar nicht klickbar ist“), und der Server
 * wählt je Ereignisart getrennt nach Anzahl vor — in einer gemeinsamen Liste fiele die
 * seltenere Art weg. Deshalb sagt auch jede Tabelle für sich, wenn sie leer ist.
 *
 * Aufbau wie die Funktionen-Tabelle (Schlüssel, Route, Art, Anzahl, Nutzer): die Route steht
 * als eigene Spalte, weil der Server je Route gruppiert; Zeilen unter fünf Personen erscheinen
 * ohne Bezeichner als Sammelzeile. Die Reihenfolge kommt vom Server (nach Anzahl).
 */
export interface WutUndTotklickTabellenProps {
  wutklicks: UsageActionRow[];
  totklicks: UsageActionRow[];
}

interface FrustTabelleProps {
  titel: string;
  testId: string;
  leerText: string;
  rows: UsageActionRow[];
}

function FrustTabelle({ titel, testId, leerText, rows }: FrustTabelleProps) {
  return (
    <Box data-testid={testId} sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {titel}
      </Typography>
      <DatenTabelle
        leer={rows.length === 0}
        leerText={leerText}
        spalten={[
          { titel: 'Schlüssel' },
          { titel: 'Route' },
          { titel: 'Art' },
          { titel: 'Anzahl', numerisch: true },
          { titel: 'Nutzer', numerisch: true },
        ]}
      >
        {rows.map((row, stelle) => (
          // Die Reihenfolge des Servers ist fest (nach Anzahl); die Stelle ist der stabile
          // Schlüssel, weil Sammelzeilen keinen Bezeichner tragen.
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={`${row.label}-${stelle}`}>
            <TableCell>
              <SchutzZeile
                suppressed={row.suppressed}
                groups={row.groups}
                users={row.users}
                bezeichner={row.name}
                technisch
              />
            </TableCell>
            <TableCell>
              {row.suppressed ? (
                '–'
              ) : (
                <SchutzZeile
                  suppressed={false}
                  groups={row.groups}
                  users={row.users}
                  bezeichner={row.route}
                  technisch
                />
              )}
            </TableCell>
            {/* Die Art steckt in `extra.element_kind`; beim Totklick ist es die Art des
                Elements, das bedienbar aussah ('button-disabled'). Sammelzeilen tragen kein
                `extra` — dort bleibt die Spalte leer, statt eine Art zu raten. */}
            <TableCell>{row.suppressed ? '–' : elementKindLabel(row.extra)}</TableCell>
            <TableCell sx={zahlenZelle}>{formatCount(row.count)}</TableCell>
            <TableCell sx={zahlenZelle}>{nutzerText(row.users, row.usersEstimated)}</TableCell>
          </TableRow>
        ))}
      </DatenTabelle>
    </Box>
  );
}

export default function WutUndTotklickTabellen({ wutklicks, totklicks }: WutUndTotklickTabellenProps) {
  return (
    <>
      <FrustTabelle
        titel="Wutklicks"
        testId="nutzung-wutklicks"
        leerText="Im Zeitraum sind keine Wutklicks verdichtet."
        rows={wutklicks}
      />
      <FrustTabelle
        titel="Totklicks"
        testId="nutzung-totklicks"
        leerText="Im Zeitraum sind keine Totklicks verdichtet."
        rows={totklicks}
      />
    </>
  );
}
